// Darstellungsregeln für die Rechnungen im Widget-Stil (design_handoff_rechnungen). Reine Funktionen,
// zusammengesetzt aus den bestehenden Regeln in lib/rechnungen — hier wird nichts fachlich neu
// entschieden, nur sichtbar gemacht (Kennzahlen, aktive Filter, Chips am Beleg, Statusfarben).
import { getFaelligkeitsBadge, getRichtung, istGuthaben, istMahnung, istMahnungOhneRechnung, mahnLabel } from '../rechnungen/sichtbarkeit';
import { wartetAufKorrektur } from '../rechnungen/korrektur';
import { formatBis } from '../rechnungen/klaerung';
import { poolGrund, type PoolBeleg } from '../rechnungen/poolGrund';

/** Felder eines Belegs, die die Darstellung liest (echte Rechnung oder Pool-Eintrag). */
export interface BelegFelder extends PoolBeleg {
  id: string;
  status?: string | null;
  brutto?: number | null;
  datum?: string | null;
  faelligkeitsdatum?: string | null;
  archiviert_am?: string | null;
  richtung?: 'eingang' | 'ausgang' | null;
  zahlungsempfaenger?: string | null;
  klaerung_wiedervorlage_am?: string | null;
  weitergeleitet_am?: string | null;
  ist_lastschrift?: boolean | null;
  skonto_prozent?: number | null;
  skonto_bis?: string | null;
  is_unklar_source?: boolean;
  mahnstufe?: number | null;
  korrektur_angefordert_am?: string | null;
  korrigiert_durch_id?: string | null;
  objektnummer?: string | null;
  _matchedObjektnummer?: string | null;
}

// ─── Format ────────────────────────────────────────────────────────────────────

/** „1.234,50 €“ wie bisher in der Liste (eurFmt). */
export const eur = (v: number | string | null | undefined): string =>
  v != null && v !== '' ? `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '';

/** TT.MM.JJJJ wie bisher (deDatumFmt). */
export const datumDe = (v: string | null | undefined): string => (v ? new Date(v).toLocaleDateString('de-DE') : '');

/** TT.MM. (Chips „→ ET“, Skonto). */
export const tagMonat = (v: string): string => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

// ─── Status ────────────────────────────────────────────────────────────────────

/** Deutsche Anzeige der Status-Filterwerte — nie Rohwerte wie „overdue“ (wie bisher in InvoicesView). */
export const STATUS_LABELS_DE: Record<string, string> = {
  alle: 'Alle', offen: 'Offen', bezahlt: 'Bezahlt', unklar: 'Unklar',
  klaerung: 'Klärung', overdue: 'Überfällig', archiviert: 'Archiviert',
  zur_pruefung: 'Zur Prüfung', zur_freigabe: 'Zur Freigabe',
};
export const statusLabelDe = (s: string): string => STATUS_LABELS_DE[s] ?? s;

/** Farbe + Fläche eines Status (Handoff §3): bezahlt grün · offen amber · unklar lila · Klärung blau · sonst grau. */
export function statusTon(status: string | null | undefined): { farbe: string; flaeche: string } {
  switch ((status ?? '').toLowerCase()) {
    case 'bezahlt': return { farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' };
    case 'offen': return { farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' };
    case 'unklar': return { farbe: 'var(--pur)', flaeche: 'var(--pur-soft)' };
    case 'klaerung': return { farbe: 'var(--info)', flaeche: 'var(--info-soft)' };
    default: return { farbe: 'var(--ink2)', flaeche: 'var(--fill2)' };
  }
}

/** Text des Status-Chips: deutsches Label, bei Klärung mit Wiedervorlage „· bis TT.MM.“. */
export function statusText(r: Pick<BelegFelder, 'status' | 'klaerung_wiedervorlage_am'>): string {
  const s = (r.status ?? '').toLowerCase();
  const label = STATUS_LABELS_DE[s] ?? (r.status || '');
  return s === 'klaerung' && r.klaerung_wiedervorlage_am ? `${label} · ${formatBis(r.klaerung_wiedervorlage_am)}` : label;
}

/** Betragsfarbe wie getBetragFarbe: Guthaben grün, überfällig rot, sonst neutral. */
export function betragFarbe(r: Pick<BelegFelder, 'brutto' | 'faelligkeitsdatum' | 'status' | 'dokument_typ'>): string {
  if (istGuthaben(r)) return 'var(--ok)';
  if (getFaelligkeitsBadge(r.faelligkeitsdatum, r.status ?? '') === 'rot') return 'var(--bad)';
  return 'var(--ink)';
}

// ─── Kennzahlen ────────────────────────────────────────────────────────────────

export interface RechnungsKennzahlen {
  offeneSum: number;
  offeneCount: number;
  overdueCount: number;
  unklarCount: number;
  /** Forderungen (eigene Ausgangsrechnungen) */
  offeneSumAusgang: number;
  /** Verbindlichkeiten (Eingangsrechnungen) */
  offeneSumEingang: number;
}

/** Kennzahl-Widgets — dieselbe Rechnung wie bisher updateStats in InvoicesView. */
export function kennzahlen(liste: readonly BelegFelder[], heute: Date = new Date()): RechnungsKennzahlen {
  // Archivierte Belege zählen nirgends mit (keine Summen, keine Auswertung)
  const list = liste.filter(r => !r.archiviert_am);
  const tag = new Date(heute);
  tag.setHours(0, 0, 0, 0);
  const lo = (r: BelegFelder) => (r.status ?? '').toLowerCase();
  const offeneItems = list.filter(r => lo(r) === 'offen' || lo(r) === 'unklar');
  const summe = (xs: BelegFelder[]) => xs.reduce((s, r) => s + (Number(r.brutto) || 0), 0);
  return {
    offeneSum: summe(offeneItems),
    offeneCount: offeneItems.length,
    overdueCount: list.filter(r => lo(r) !== 'bezahlt' && lo(r) !== 'klaerung' && !!r.faelligkeitsdatum && new Date(r.faelligkeitsdatum) < tag).length,
    // "Prüfung nötig" = Status unklar (beide Tabellen) — NICHT die Herkunft is_unklar_source.
    unklarCount: list.filter(r => lo(r) === 'unklar').length,
    offeneSumAusgang: summe(offeneItems.filter(r => getRichtung(r) === 'ausgang')),
    offeneSumEingang: summe(offeneItems.filter(r => getRichtung(r) === 'eingang')),
  };
}

// ─── Aktive Filter ─────────────────────────────────────────────────────────────

export interface FilterZustand {
  objektName: string | null;
  status: string;
  zustaendig: string;
  suche: string;
  nurMahnungen: boolean;
  nurKorrektur: boolean;
  nurGuthaben: boolean;
  nurSepaSkonto: boolean;
  datumVon: string;
  datumBis: string;
}

export type FilterChipKey = 'objekt' | 'status' | 'zustaendig' | 'suche' | 'mahnung' | 'korrektur' | 'guthaben' | 'sepa' | 'zeitraum';
export interface FilterChip { key: FilterChipKey; label: string }

/** Name der gewählten Zuständigkeit im Chip — wie bisher: Global, Teamname, Mitarbeiter, sonst „Ich“. */
export function zustaendigFilterName(wert: string, mitglieder: readonly { id: string; full_name: string }[], teams: readonly { id: string; name: string }[]): string {
  if (wert === 'global') return 'Global';
  if (wert.startsWith('t:')) return teams.find(t => t.id === wert.slice(2))?.name || 'Team';
  return mitglieder.find(m => m.id === wert)?.full_name || 'Ich';
}

/** Chips der Leiste „Aktive Filter:“ in der bisherigen Reihenfolge; leer = Leiste ausblenden. */
export function aktiveFilterChips(z: FilterZustand, zustaendigName: string): FilterChip[] {
  const chips: FilterChip[] = [];
  if (z.objektName !== null) chips.push({ key: 'objekt', label: `Objekt: ${z.objektName}` });
  if (z.status !== 'alle') chips.push({ key: 'status', label: `Status: ${statusLabelDe(z.status)}` });
  if (z.zustaendig !== 'alle') chips.push({ key: 'zustaendig', label: `Zuständig: ${zustaendigName}` });
  if (z.suche) chips.push({ key: 'suche', label: `Suche: ${z.suche}` });
  if (z.nurMahnungen) chips.push({ key: 'mahnung', label: 'Nur Mahnungen' });
  if (z.nurKorrektur) chips.push({ key: 'korrektur', label: 'Korrektur angefordert' });
  if (z.nurGuthaben) chips.push({ key: 'guthaben', label: 'Nur Guthaben' });
  if (z.nurSepaSkonto) chips.push({ key: 'sepa', label: 'Nur SEPA / Skonto' });
  if (z.datumVon || z.datumBis) {
    chips.push({ key: 'zeitraum', label: `Zeitraum: ${z.datumVon ? datumDe(z.datumVon) : '…'} – ${z.datumBis ? datumDe(z.datumBis) : '…'}` });
  }
  return chips;
}

// ─── Karte / Ansicht ───────────────────────────────────────────────────────────

/** Ab dieser Breite des Fensters Tabelle, darunter Karten (Handoff §8). */
export const TABELLEN_AB = 1200;
export const ansichtFuerBreite = (breite: number): 'tabelle' | 'karten' => (breite >= TABELLEN_AB ? 'tabelle' : 'karten');

/** Kopfzeile der Karte „Nummer · Datum“ — leere Teile fallen weg. */
export const kartenKopf = (nummer: string | null | undefined, datum: string | null | undefined): string =>
  [nummer, datum].filter((t): t is string => !!t && t.trim() !== '').join(' · ');

/** Metazeile der Karte „Brutto · Objektnr · Haus · fällig …“ — leere Teile fallen weg. */
export function kartenMeta(r: BelegFelder, hausName: string): string {
  return [eur(r.brutto), r.objektnummer || r._matchedObjektnummer || '', hausName, r.faelligkeitsdatum ? `fällig ${datumDe(r.faelligkeitsdatum)}` : '']
    .filter(Boolean).join(' · ');
}

// ─── Chips am Beleg ────────────────────────────────────────────────────────────

export type ChipTon = 'et' | 'info' | 'ok' | 'pur' | 'mahn';
export interface BelegChip { key: string; label: string; tip: string; ton: ChipTon }
export interface BelegSprung { key: 'nachfolger' | 'vorgaenger'; label: string; tip: string; zielId: string | null }

export const CHIP_FARBE: Record<ChipTon, { farbe: string; flaeche: string }> = {
  et: { farbe: '#6366f1', flaeche: 'rgba(99,102,241,0.13)' },
  info: { farbe: 'var(--info)', flaeche: 'var(--info-soft)' },
  ok: { farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' },
  pur: { farbe: 'var(--pur)', flaeche: 'var(--pur-soft)' },
  mahn: { farbe: '#e8730c', flaeche: 'rgba(232,115,12,0.14)' },
};

/** Chips am Beleg — dieselben Bedingungen und Texte wie bisher in der Tabellenzeile. */
export function belegChips(r: BelegFelder): BelegChip[] {
  const chips: BelegChip[] = [];
  if (r.weitergeleitet_am) {
    chips.push({ key: 'et', label: `→ ET · ${tagMonat(r.weitergeleitet_am)}`, tip: `Am ${datumDe(r.weitergeleitet_am)} an den Eigentümer weitergeleitet`, ton: 'et' });
  }
  if (r.ist_lastschrift) chips.push({ key: 'sepa', label: 'SEPA', tip: 'SEPA-Lastschrift', ton: 'info' });
  if (r.skonto_prozent != null) {
    chips.push({ key: 'skonto', label: `Skonto ${Number(r.skonto_prozent).toLocaleString('de-DE')}%${r.skonto_bis ? ` bis ${tagMonat(r.skonto_bis)}` : ''}`, tip: 'Skonto', ton: 'ok' });
  }
  if (r.is_unklar_source) {
    const g = poolGrund(r);
    chips.push({ key: 'pool', label: `KI-Pool · ${g.titel}`, tip: `KI-Pool – ${g.text}`, ton: 'pur' });
  }
  if (istGuthaben(r)) chips.push({ key: 'guthaben', label: 'Guthaben', tip: 'Guthaben', ton: 'ok' });
  if (istMahnung(r)) {
    const ohne = istMahnungOhneRechnung(r);
    const label = ohne ? 'Mahnung ohne Rechnung' : mahnLabel(r) ?? '';
    chips.push({ key: 'mahnung', label, tip: ohne ? 'Mahnung ohne Rechnung – Zu dieser Mahnung wurde keine Rechnung gefunden — bitte von Hand zuordnen' : label, ton: 'mahn' });
  }
  if (wartetAufKorrektur(r)) chips.push({ key: 'korrektur', label: 'Korrektur angefordert', tip: 'Korrektur angefordert', ton: 'info' });
  return chips;
}

/** Sprünge „Ersetzt durch NR“ / „Ersetzt NR“ (beide Richtungen der Korrektur-Verknüpfung). */
export function belegSpruenge(
  r: Pick<BelegFelder, 'id' | 'korrigiert_durch_id'>,
  partner: { nachfolger: ReadonlyMap<string, { id: string; nummer?: string }>; vorgaenger: ReadonlyMap<string, { id: string; nummer?: string }> },
): BelegSprung[] {
  const spruenge: BelegSprung[] = [];
  if (r.korrigiert_durch_id) {
    const n = partner.nachfolger.get(r.id);
    spruenge.push(n?.nummer
      ? { key: 'nachfolger', label: `Ersetzt durch ${n.nummer}`, tip: 'Zur korrigierten Rechnung springen', zielId: r.korrigiert_durch_id }
      : { key: 'nachfolger', label: 'Ersetzt', tip: '', zielId: null });
  }
  const v = partner.vorgaenger.get(r.id);
  if (v) spruenge.push({ key: 'vorgaenger', label: `Ersetzt ${v.nummer || ''}`.trim(), tip: 'Zur ersetzten Rechnung springen', zielId: v.id });
  return spruenge;
}
